import React, { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "./context/AuthContext";
import { ProgressSpinner } from "primereact/progressspinner";

const PrivateRoutes = () => {
  const navigate = useNavigate();
  const { currentUser, isAdminUser, isAdminLoading, loading } = useAuth();

  const [dialogVisible, setDialogVisible] = useState(false);

  // We can't decide access until Firebase has restored the session (`loading`)
  // and — for a signed-in user — until the admin record has been fetched
  // (`isAdminLoading`). Showing the dialog before then is what caused the
  // "not authorized" flash on hard refresh, since currentUser is briefly null.
  const isResolving = loading || (!!currentUser && isAdminLoading);

  useEffect(() => {
    if (!isResolving && !(currentUser && isAdminUser)) {
      setDialogVisible(true);
    }
  }, [isResolving, currentUser, isAdminUser]);

  const hideDialog = () => {
    setDialogVisible(false);
    navigate("/datasets");
  };

  if (isResolving) {
    return (
      <div className="h-screen flex justify-content-center items-center">
        <ProgressSpinner style={{ width: "250px", height: "250px" }} strokeWidth="6" />
      </div>
    );
  }

  // Render Outlet if authenticated, otherwise navigate to the datasets page

  return currentUser && isAdminUser ? (
    <Outlet />
  ) : (
    <div className="card flex justify-content-center h-screen">
      <Dialog
        modal
        header="Authentication Required"
        position={"middle"}
        footer={
          <Button
            className="w-full inline-flex justify-center rounded-md shadow-sm px-4 py-2 bg-red-500 text-base text-white hover:bg-red-700 sm:ml-3 sm:w-auto sm:text-sm"
            label="Close"
            onClick={hideDialog}
            autoFocus
          />
        }
        style={{ width: "500px" }}
        visible={dialogVisible}
        onHide={hideDialog}
      >
        <p>You are not authorized to view this page. Please login to an authorized account first.</p>
      </Dialog>
    </div>
  );
};

// Restrict route to unauthorized user

const ApplicationRoute = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [loading, setLoading] = useState(true); // State to manage loading

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 2000);

    // Clean up the timeout
    return () => clearTimeout(timer);
  }, []);

  const hideDialog = () => {
    navigate("/datasets");
  };

  if (loading) {
    return (
      <div className="h-screen flex justify-content-center items-center">
        <ProgressSpinner style={{ width: "250px", height: "250px" }} strokeWidth="6" />
      </div>
    );
  }

  // Render Outlet if authenticated, otherwise navigate to the datasets page

  if (!currentUser) {
    return (
      <div className="card flex justify-content-center h-screen">
        <Dialog
          modal
          style={{ width: "500px" }}
          header="Authentication Required"
          position={"middle"}
          footer={
            <Button
              className="w-full inline-flex justify-center rounded-md shadow-sm px-4 py-2 bg-red-500 text-base text-white hover:bg-red-700 sm:ml-3 sm:w-auto sm:text-sm"
              label="Close"
              onClick={hideDialog}
              autoFocus
            />
          }
          visible={true} // Always show the dialog if currentUser is null
          onHide={hideDialog}
        >
          <p>You are not authorized to view this page. Please login to an authorized account first.</p>
        </Dialog>
      </div>
    );
  }

  // Render Outlet if authenticated
  return <Outlet />;
};

export { PrivateRoutes, ApplicationRoute };
